/* eslint-disable @typescript-eslint/naming-convention */
import { Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { AbstractControl, UntypedFormBuilder, UntypedFormGroup } from '@angular/forms';

import {
  catchError,
  combineLatest,
  concatMap,
  filter,
  finalize,
  of,
  Subscription,
  switchMap,
  take,
  tap,
} from 'rxjs';
import moment, { Moment } from 'moment-timezone';
import { Angulartics2 } from 'angulartics2';

import { PaymentPlanCodes, PlanPeriod, PlanTier } from '@shared/price-plan/enums';
import { PricePlan } from '@shared/price-plan/models';
import { ISubscriptionChange, RecurlySubscription } from '@shared/recurly/models';
import { SubscriptionState } from '@shared/recurly/enums';
import { SubscriptionTypes } from '@shared/restaurant/enums';
import { ChangeSubscriptionRequest, LocationRecurly } from '@shared/restaurant/models';
import { Account, AccountRecurly } from '@shared/account/models';
import { AuthUser } from '@shared/signup/models';
import { Location } from '@shared/location/models';
import { UserAccountRole, UserLocationRole, UserRole } from '@shared/user/enums';
import { FrontendModalService } from '@tablesready/frontend-modal';
import { AppIcon, AppIconColor } from '@tablesready/frontend-icons';
import {
  AuthRestaurantService,
  LocationsService,
  PricePlansQueryService
} from '@tablesready/frontend-store';
import { StatusService } from '@tablesready/frontend-status-bar';
import { AlertsService, ClockService } from '@tablesready/frontend-alerts-bar';
import { APP_VARIABLES_TOKEN, AppVariables } from '@tablesready/frontend-variables';
import { PermissionService } from '@tablesready/frontend-permissions';
import { FrontendBreakpointsService } from '@tablesready/frontend-breakpoints';
import {
  TRIAL_LIMIT,
  OLD_PLANS_LIMIT,
  PricePlansService,
  GlobalErrorHandler,
  ENTERPRISE_PRICE_PLAN,
  ConfirmationModalService,
  SwitchPlansRestrictionsService,
} from '@tablesready/common-ui';
import { RecurlyService } from '../services';
import { PlanLimit } from './models';
import {
  CancelSubscriptionComponent,
  PauseSubscriptionComponent,
  ReactivateSubscriptionComponent,
} from './modals';
import { BillingService, SubscriptionService, } from './services';
import { PLAN_TIERS } from './constants';

@Component({
  selector: 'tablesready-billing',
  templateUrl: './billing.component.html',
  styleUrls: ['./scss/billing.component.scss'],
  providers: [GlobalErrorHandler],
})
export class BillingComponent implements OnInit, OnDestroy {
  activePlanTier: PlanTier = PlanTier.free;
  selectedStarterLimit: number;
  selectedBusinessLimit: number;
  starterPlansLimits: PlanLimit[];
  businessPlansLimits: PlanLimit[];
  selectedStarterPlan: PricePlan;
  selectedBusinessPlan: PricePlan;
  starterPlans: PricePlan[] = [];
  businessPlans: PricePlan[] = [];
  starterPlansByPeriod: PricePlan[] = [];
  businessPlansByPeriod: PricePlan[] = [];

  who?: AuthUser;
  account?: Account;
  location?: Location;
  planTiers = PlanTier;
  planPeriods = PlanPeriod;
  locationRecurly?: LocationRecurly;
  accountRecurly?: AccountRecurly;
  freePlan?: PricePlan;
  isAccountAdmin = false;
  trial: any;
  timezone: string;
  inProgress = false;
  selectPricePlanForm?: UntypedFormGroup;
  subscriptionStates = SubscriptionState;
  subscriptionType = SubscriptionTypes;
  isNotUsaOrCanadaRestaurant = false;
  pendingSubscriptionChange: ISubscriptionChange = null;
  subscriptionState: SubscriptionState;
  selectedLimit: number;
  subscriptionPausedText: string;
  clock = Date.now();
  planCodes = PaymentPlanCodes;
  noBillingInfo: boolean;
  currentPricePlan: PricePlan;
  subscriptionResumeAt: string;
  locationSubscriptionId?: string;
  oldPlanLimit: number;
  enterPriseLimit = ENTERPRISE_PRICE_PLAN.limit;
  icon = AppIcon;
  iconColor = AppIconColor;
  periodButtonActiveClasses = ['active', 'color-primary', 'border-accent'];
  periodButtonInactiveClasses = ['color-primary', 'border-primary-text', 'opaque-text'];
  isOffline = false;
  blockActions = false;
  countOfFreeLocations = 0;
  recurlyKey?: string;
  currentSubscription: RecurlySubscription;
  isMobile = false;
  canceledSubscriptionChange: boolean;
  planTiersMap = PLAN_TIERS;

  private isLocationManager = false;
  private readonly defaultMessagesLimit = 150;
  private readonly subscriptions = new Subscription();

  constructor(
    private readonly modalController: FrontendModalService,
    private readonly alertsService: AlertsService,
    private readonly pricePlansQueryService: PricePlansQueryService,
    private readonly clockService: ClockService,
    private readonly statusService: StatusService,
    private readonly recurlyService: RecurlyService,
    private readonly fb: UntypedFormBuilder,
    private readonly subscriptionService: SubscriptionService,
    private readonly permissions: PermissionService,
    private readonly authRestaurantService: AuthRestaurantService,
    private readonly locationsService: LocationsService,
    private readonly angulartics2: Angulartics2,
    private readonly errorHandler: GlobalErrorHandler,
    private readonly breakpointService: FrontendBreakpointsService,
    private readonly confirmationModalService: ConfirmationModalService,
    private readonly billingService: BillingService,
    private readonly pricePlansService: PricePlansService,
    private readonly switchPlansRestrictionsService: SwitchPlansRestrictionsService,
    @Inject(APP_VARIABLES_TOKEN) public readonly appVariables: AppVariables,
  ) { }

  ngOnInit(): void {
    const clockSub = this.clockService.clock$.subscribe((now) => {
      this.clock = now;
    });
    this.subscriptions.add(clockSub);

    this.subscriptions.add(this.pricePlansQueryService.listenToChanges().subscribe());

    const authSub = combineLatest([
      this.authRestaurantService.selectWithAccountAndLocation(),
      this.recurlyService.getApiKey().pipe(tap(key => this.recurlyKey = String(key))),
    ])
      .pipe(
        filter(([user]) => {
          this.isOffline = user.account.subscriptionType === SubscriptionTypes.offline;

          return !this.isOffline;
        }),
        tap(([user]) => this.init(user)),
        concatMap(() => this.subscriptionService.checkSubscription(this.who.location.recurly.subscriptionUuid)),
        tap(currentPricePlan => this.initSubscriptionData(currentPricePlan)),
      ).subscribe();
    this.subscriptions.add(authSub);

    const mobileSub = this.breakpointService
      .observeXsBreakpoint()
      .subscribe((isMobile) => this.isMobile = isMobile);
    this.subscriptions.add(mobileSub);
  }

  init(who: AuthUser): void {
    this.who = who;
    this.account = who.account;
    this.location = who.location;
    this.isAccountAdmin = this.permissions.isAccountAdmin(this.who);
    this.isLocationManager = who.accountUser && who.accountUser.role === UserAccountRole.user &&
      who.locationUser && who.locationUser.role === UserLocationRole.manager;

    const locationSubUuid = this.location.recurly.subscriptionUuid;
    const locationSub = this.account.recurly.subscriptions.find(sub => sub.uuid === locationSubUuid);
    if (locationSub !== undefined) {
      this.locationSubscriptionId = locationSub.id;
    }

    this.initRestaurantData();
    this.initForm();
  }

  setPlanType(type: PlanPeriod): void {
    const control = this.selectPricePlanForm.get('planType');
    if (!control) {
      return;
    }

    control.setValue(type);
  }

  get canceledSubscription(): boolean {
    return this.canceledSubscriptionChange ||
      this.subscriptionState === SubscriptionState.canceled;
  }

  get planTypeControl(): AbstractControl {
    return this.selectPricePlanForm.get('planType');
  }

  get showPremiumPlanAlert(): boolean {
    const trialLimit = this.isNotUsaOrCanadaRestaurant ? TRIAL_LIMIT / 10 : TRIAL_LIMIT;
    const notAllowedSubscriptionStates = [
      SubscriptionState.canceled,
      SubscriptionState.expired,
      SubscriptionState.paused,
    ];

    if (this.who?.account && this.who?.location) {
      return this.who.role !== UserRole.admin &&
        !this.who.location.recurly.trial &&
        this.who.location.recurly.planCode !== PaymentPlanCodes.Free &&
        this.who.location.usage.limit !== trialLimit &&
        this.who.account.subscriptionType === SubscriptionTypes.recurly &&
        !notAllowedSubscriptionStates.includes(this.who.location.recurly.subscription_state);
    }

    return false;
  }

  getPaymentPeriod(subscriptionChange: ISubscriptionChange): string {
    const pricePlan = this.pricePlansService.getByPlanCode(subscriptionChange.code, this.isNotUsaOrCanadaRestaurant);

    return pricePlan?.plan_interval_length === 12 ? 'year' : 'mo';
  }

  cancelScheduledPause(): void {
    const options = {
      uuid: this.currentSubscription.uuid,
      pauseCycles: 0,
    };

    this.inProgress = true;
    this.blockActions = true;

    const pauseSub = this.subscriptionService.pause(options)
      .pipe(
        tap(() => {
          this.inProgress = false;
          this.alertsService.addSuccess(
            'scheduled subscription pause was canceled.',
            'Success: ',
          );
        }),
        catchError((error) => {
          this.inProgress = false;
          this.alertsService.addError(
            String(error),
            'Error: ',
          );

          return of(error);
        }),
        finalize(() => this.unblockActions()),
      )
      .subscribe();

    this.subscriptions.add(pauseSub);
  }

  onLimitSelected(limit: number, tier = PlanTier.starter): void {
    if (tier === PlanTier.starter) {
      if (limit === this.selectedStarterLimit) {
        return;
      }

      this.selectedStarterLimit = limit;
    }

    if (tier === PlanTier.business) {
      if (limit === this.selectedBusinessLimit) {
        return;
      }

      this.selectedBusinessLimit = limit;
    }

    this.selectedLimit = limit;
    this.changeMessagesLimit(tier);
  }

  pauseSubscription(): void {
    this.blockActions = true;
    this.modalController.create(PauseSubscriptionComponent, {
      pricePlan: this.currentPricePlan,
      timezone: this.who.location.timezone,
      sub: this.currentSubscription,
      onClose: (): void => {
        this.unblockActions();
      },
    })
      .pipe(
        take(1),
        finalize(() => {
          this.unblockActions();
        }),
      )
      .subscribe();
  }

  cancelSubscription(): void {
    this.blockActions = true;
    this.modalController.create(CancelSubscriptionComponent, {
      sub: this.currentSubscription,
      onClose: (): void => {
        this.unblockActions();
      },
    })
      .pipe(
        take(1),
        finalize(() => {
          this.unblockActions();
        }),
      )
      .subscribe();
  }

  reactivateSubscription(): void {
    this.blockActions = true;
    this.modalController.create(ReactivateSubscriptionComponent, {
      sub: this.currentSubscription,
      onClose: (): void => {
        this.unblockActions();
      },
    })
      .pipe(
        take(1),
        finalize(() => {
          this.unblockActions();
        }),
      )
      .subscribe();
  }

  getPendingSubscriptionChange(uuid: string): void {
    const pendingSubscriptionChangeSub = this.recurlyService.getPendingSubscriptionChange(uuid)
      .pipe(tap((response) => {
        this.pendingSubscriptionChange = response;
      }))
      .subscribe();
    this.subscriptions.add(pendingSubscriptionChangeSub);
  }

  switchPlans(planCode: string): void {
    const isCurrentPricePlanFree = this.currentPricePlan.tier === PlanTier.free;
    const newPricePlan = this.pricePlansService.getByPlanCode(planCode, this.isNotUsaOrCanadaRestaurant);
    const isNewPlanFree = newPricePlan.tier === PlanTier.free;
    const tierChanged = this.currentPricePlan.tier !== newPricePlan.tier;
    const limitChanged = this.currentPricePlan.limit !== newPricePlan.limit;
    const periodChanged = this.currentPricePlan.plan_interval_length !== newPricePlan.plan_interval_length;

    this.blockActions = true;

    if (!isCurrentPricePlanFree && !isNewPlanFree && (tierChanged || limitChanged) && periodChanged) {
      this.switchPlansRestrictionsService.unableSwitchPlans(this.currentPricePlan, newPricePlan, this.isNotUsaOrCanadaRestaurant);
      this.unblockActions();

      return;
    }

    if (isCurrentPricePlanFree) {
      this.switchPlanFromCurrentFreeSubscription(planCode);

      return;
    }

    this.switchCurrentPaymentPlan(planCode);
  }

  changeMessagesLimit(tier = PlanTier.starter): void {
    if (tier === PlanTier.starter) {
      const minStarterLimit = Math.min(...this.starterPlansByPeriod.map(plan => plan.limit));
      const recommendedStarterPlan = this.starterPlansByPeriod.find(plan => plan.limit === this.selectedStarterLimit);

      this.selectedStarterPlan = recommendedStarterPlan;

      if (!recommendedStarterPlan) {
        this.selectedStarterPlan = this.starterPlansByPeriod.find(plan => plan.limit === minStarterLimit);
      }
    }

    if (tier === PlanTier.business) {
      const minBusinessLimit = Math.min(...this.businessPlansByPeriod.map(plan => plan.limit));
      const recommendedBusinessPlan = this.businessPlansByPeriod.find(plan => plan.limit === this.selectedBusinessLimit);

      this.selectedBusinessPlan = recommendedBusinessPlan;

      if (!recommendedBusinessPlan) {
        this.selectedBusinessPlan = this.businessPlansByPeriod.find(plan => plan.limit === minBusinessLimit);
      }
    }
  }

  activateSubscription(plan: string): void {
    this.inProgress = true;

    if (this.noBillingInfo) {
      this.billingService.addBillingData(this.who, this.recurlyKey)
        .pipe(
          filter(response => {
            if (!response) {
              this.unblockActions();
            }

            return response;
          }),
          switchMap(() => this.subscriptionService.activate({
            plan,
            coupon: '',
          })),
          take(1),
          tap(() => this.activateSubscriptionSuccess()),
          catchError(error => {
            this.alertsService.addError(
              String(error),
              'Error: ',
              10000,
              this.isLocationManager
            );

            return of(null);
          }),
          finalize(() => {
            this.inProgress = false;
            this.unblockActions();
          }),
        ).subscribe();

      return;
    }

    this.subscriptionService.activate({
      plan,
      coupon: '',
    })
      .pipe(
        take(1),
        tap(() => this.activateSubscriptionSuccess()),
        catchError(error => {
          this.alertsService.addError(
            String(error),
            'Error: ',
            10000,
            this.isLocationManager
          );

          return of(null);
        }),
        finalize(() => {
          this.inProgress = false;
          this.unblockActions();
        }),
      ).subscribe();
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  isNotAvailableFreeSubscriptions(subscriptionChange: ISubscriptionChange): boolean {
    const pricePlan = this.pricePlansService.getByPlanCode(subscriptionChange.code, this.isNotUsaOrCanadaRestaurant);

    return pricePlan.plan_code === PaymentPlanCodes.Free && this.countOfFreeLocations >= this.appVariables.FREE_PLAN_LOCATION_LIMIT;
  }

  calculateSavePercentage = (tier: PlanTier, limit: number): number => {
    let monthlyPlan;
    let annualPlan;

    if (tier === PlanTier.starter) {
      monthlyPlan = this.starterPlans.find(plan => plan.plan_interval_length === 1 && plan.limit === limit);
      annualPlan = this.starterPlans.find(plan => plan.plan_interval_length === 12 && plan.limit === limit);
    } else {
      monthlyPlan = this.businessPlans.find(plan => plan.plan_interval_length === 1 && plan.limit === limit);
      annualPlan = this.businessPlans.find(plan => plan.plan_interval_length === 12 && plan.limit === limit);
    }

    if (!monthlyPlan || !annualPlan) {
      return 0;
    }

    const annualPriceCalculatedMonthly = Math.round((annualPlan.price / 100) / 12);
    return 100 - Math.round(annualPriceCalculatedMonthly / (monthlyPlan.price / 100) * 100);
  }


  private initForm(): void {
    if (this.who.account.subscriptionType === SubscriptionTypes.offline) {
      return;
    }

    this.selectPricePlanForm = this.fb.group({ planType: [PlanPeriod.Monthly] });

    if (this.location.recurly.planCode.includes('annual')) {
      this.planTypeControl.setValue(PlanPeriod.Annual);
    }

    const changePlanTypeSub = this.planTypeControl.valueChanges.subscribe((period: PlanPeriod) => {
      this.getPlansByPeriod(period);

      const minStarterPlanLimit = Math.min(...this.starterPlansLimits.map(limit => limit.value));
      const minBusinessPlanLimit = Math.min(...this.businessPlansLimits.map(limit => limit.value));

      if (this.who.location.recurly.subscription_state === SubscriptionState.paused) {
        if (this.currentPricePlan.tier === PlanTier.starter) {
          this.selectedStarterLimit = this.currentPricePlan.plan_interval_length === period
            ? this.currentPricePlan.limit
            : minStarterPlanLimit;
        }

        if (this.currentPricePlan.tier === PlanTier.business || this.currentPricePlan.tier === PlanTier.premium) {
          this.selectedStarterLimit = this.currentPricePlan.plan_interval_length === period
            ? this.currentPricePlan.limit
            : minBusinessPlanLimit;
        }
      }

      let starterPlanToSet = this.starterPlansByPeriod.find(plan => plan.limit === this.selectedStarterLimit);
      let businessPlanToSet = this.businessPlansByPeriod.find(plan => plan.limit === this.selectedBusinessLimit);

      if (!starterPlanToSet) {
        starterPlanToSet = this.starterPlansByPeriod.find(plan => plan.limit === minStarterPlanLimit);
        this.selectedStarterLimit = minStarterPlanLimit;
      }

      if (!businessPlanToSet) {
        businessPlanToSet = this.businessPlansByPeriod.find(plan => plan.limit === minBusinessPlanLimit);
        this.selectedBusinessLimit = minBusinessPlanLimit;
      }

      if (this.selectedStarterLimit) {
        starterPlanToSet = this.starterPlansByPeriod.find(plan => plan.limit === this.selectedStarterLimit);
      }

      if (this.selectedBusinessLimit) {
        businessPlanToSet = this.businessPlansByPeriod.find(plan => plan.limit === this.selectedBusinessLimit);
      }

      this.selectedStarterPlan = starterPlanToSet;
      this.selectedBusinessPlan = businessPlanToSet;
    });

    this.subscriptions.add(changePlanTypeSub);
  }

  private initSubscriptionData(currentPricePlan: PricePlan,): void {
    this.countOfFreeLocations = this.locationsService.getCountOfFreeLocations();
    this.oldPlanLimit = this.isNotUsaOrCanadaRestaurant ? OLD_PLANS_LIMIT / 10 : OLD_PLANS_LIMIT;
    this.starterPlans = this.pricePlansService.getByTier(PlanTier.starter, this.isNotUsaOrCanadaRestaurant);
    this.businessPlans = this.pricePlansService.getByTier(PlanTier.business, this.isNotUsaOrCanadaRestaurant);
    this.freePlan = this.pricePlansService.getFree(this.isNotUsaOrCanadaRestaurant);
    this.currentPricePlan = {
      ...currentPricePlan,
      limit: this.isNotUsaOrCanadaRestaurant ? Math.ceil(currentPricePlan.limit * 0.1) : currentPricePlan.limit,
    };
    this.activePlanTier = currentPricePlan.tier;
    this.canceledSubscriptionChange = this.who?.location.recurly.canceledSubscriptionChange;

    let planPeriod = this.currentPricePlan.plan_interval_length === 1 ? PlanPeriod.Monthly : PlanPeriod.Annual;

    if (this.currentPricePlan.tier === PlanTier.free) {
      planPeriod = PlanPeriod.Monthly;
    }

    this.getPlansByPeriod(planPeriod);
    this.subscriptionState = this.who.location.recurly.subscription_state;

    if (this.currentSubscription?.paused_at && Date.now() < this.currentSubscription?.paused_at) {
      const { expiration, restart } = this.getPausedSubscriptionData();
      this.subscriptionPausedText = `Your subscription will pause from <strong>${expiration.format('L')}</strong> and will resume automatically on <strong>${restart.format('L')}</strong>. `;
    }

    if (this.who?.location.recurly?.subscription_state === SubscriptionState.paused) {
      const { restart } = this.getPausedSubscriptionData();
      this.subscriptionResumeAt = restart.format('L');
    }

    if (this.currentPricePlan.tier === PlanTier.free) {
      this.selectedStarterLimit = this.starterPlansLimits[0].value;
      this.selectedBusinessLimit = this.businessPlansLimits[0].value;
    }

    if (this.currentPricePlan.tier === PlanTier.premium || this.currentPricePlan.tier === PlanTier.business) {
      this.selectedBusinessLimit = this.currentPricePlan.limit;
      this.selectedStarterLimit = this.starterPlansLimits[0].value;
    }

    if (this.currentPricePlan.tier === PlanTier.starter) {
      this.selectedBusinessLimit = this.businessPlansLimits[0].value;
      this.selectedStarterLimit = this.currentPricePlan.limit;
    }

    this.selectedStarterPlan = this.starterPlansByPeriod.find(plan => plan.limit === this.selectedStarterLimit);
    this.selectedBusinessPlan = this.currentPricePlan.tier === PlanTier.premium
      ? this.currentPricePlan
      : this.businessPlansByPeriod.find(plan => plan.limit === this.selectedBusinessLimit);
  }

  private initRestaurantData(): void {
    this.timezone = this.who.location.timezone;
    this.locationRecurly = this.who.location.recurly;
    this.accountRecurly = this.who.account.recurly;
    this.noBillingInfo = this.accountRecurly?.billing_info === undefined;

    this.trial = this.statusService.getDaysLeft(this.who.account.expirationDate);
    this.statusService.updateStatusBarAlerts(this.who);
    this.isNotUsaOrCanadaRestaurant = !['US', 'CA'].includes(this.who.location.country);

    if (this.isNotUsaOrCanadaRestaurant) {
      this.enterPriseLimit = ENTERPRISE_PRICE_PLAN.limit / 10;
    }

    this.subscriptionPausedText = null;

    if (this.who.role !== UserRole.admin && this.who.account.subscriptionType === SubscriptionTypes.recurly) {
      this.currentSubscription = this.accountRecurly.subscriptions.find(sub => sub.uuid === this.locationRecurly.subscriptionUuid);
      const currentPlanCode = this.locationRecurly.planCode;

      if (!currentPlanCode) {
        this.currentSubscription = this.accountRecurly.subscriptions[0];
      }

      if (
        this.who.account.subscriptionType === SubscriptionTypes.recurly &&
        this.locationRecurly.subscriptionUuid
      ) {
        this.getPendingSubscriptionChange(this.locationRecurly.subscriptionUuid);
      }
    }
  }

  private unblockActions(): void {
    this.blockActions = false;
  }

  private getPlansByPeriod(period: PlanPeriod): void {
    let starterPlansLimits: PlanLimit[] = [];
    let businessPlansLimits: PlanLimit[] = [];

    this.starterPlansByPeriod = [];
    this.businessPlansByPeriod = [];

    for (const plan of this.starterPlans) {
      if (plan.plan_interval_length === period) {
        this.starterPlansByPeriod.push(plan);
        starterPlansLimits.push(this.billingService.preparePricePlanLimit(plan))
      }
    }

    for (const plan of this.businessPlans) {
      if (plan.plan_interval_length === period) {
        this.businessPlansByPeriod.push(plan);
        businessPlansLimits.push(this.billingService.preparePricePlanLimit(plan))
      }
    }

    const { availableForManager, enabled, outdated, tier } = this.currentPricePlan;

    if (!availableForManager || !enabled || outdated) {
      if (tier === PlanTier.business || tier === PlanTier.premium) {
        businessPlansLimits = [
          ...businessPlansLimits,
          this.billingService.preparePricePlanLimit(this.currentPricePlan),
        ]
      }

      if (tier === PlanTier.starter) {
        starterPlansLimits = [
          ...starterPlansLimits,
          this.billingService.preparePricePlanLimit(this.currentPricePlan),
        ];
      }
    }

    this.starterPlansLimits = this.billingService.sortPlanLimits(starterPlansLimits, this.freePlan.limit);
    this.businessPlansLimits = this.billingService.sortPlanLimits(businessPlansLimits, this.freePlan.limit);
  }

  private activateSubscriptionSuccess(): void {
    this.alertsService.addSuccess('Premium plan activated successfully');
    this.angulartics2.eventTrack.next({
      action: 'Create New Subscription',
      properties: {
        category: 'account',
      },
    });
  }

  private getPausedSubscriptionData(): { expiration: Moment; restart: Moment } {
    const expiration = moment(this.currentSubscription?.expiration_date).tz(this.timezone);
    let duration = this.currentPricePlan.plan_interval_length;
    if (this.currentPricePlan.plan_code.includes('annual')) {
      duration = 12; // eslint-disable-line no-magic-numbers
    }
    const restart = expiration.clone().add(duration * this.currentSubscription.remaining_pause_cycles, 'M');

    return { expiration, restart };
  }

  private switchPlanFromCurrentFreeSubscription(plan: string): void {
    const trialUsed = this.accountRecurly.trial !== undefined;
    const changeOptions: ChangeSubscriptionRequest = {
      planCode: plan,
      uuid: this.currentSubscription.uuid,
    }
    this.blockActions = true;

    const trialLimit = this.isNotUsaOrCanadaRestaurant ? Math.ceil(this.defaultMessagesLimit * 0.1) : this.defaultMessagesLimit;
    const { tier } = this.pricePlansService.getByPlanCode(plan, this.isNotUsaOrCanadaRestaurant);
    const options = {
      title: 'Free trial period',
      content: `You'll activate a 14 day free trial period. You'll have
        full access to ${this.billingService.getAccountFeaturesByTier(this.account.features, tier)} features, except for the
        message limit, which will be limited to ${trialLimit} messages.`,
      confirmBtnText: 'Confirm',
      closeBtnText: 'Cancel',
    }

    if (trialUsed) {
      if (this.noBillingInfo) {
        this.billingService.addBillingData(this.who, this.recurlyKey)
          .pipe(
            filter(response => {
              if (!response) {
                this.unblockActions();
              }

              return response;
            }),
            switchMap(() => this.subscriptionService.change(changeOptions)),
            tap(() => this.activateSubscriptionSuccess()),
            finalize(() => this.unblockActions()),
          ).subscribe();

        return;
      }

      const pricePlan = this.pricePlansService.getByPlanCode(plan, this.isNotUsaOrCanadaRestaurant);
      options.title = 'Update your subscription';
      options.content = this.billingService.prepareChangeSubscriptionPlanConfirmationText(
        pricePlan,
        this.currentPricePlan,
        this.account.features,
        this.location.usage.total,
        this.account.recurly.trial === true,
      );

      this.confirmationModalService.confirm(options)
        .pipe(
          filter(({ isConfirmed }) => {
            if (!isConfirmed) {
              this.unblockActions();
            }

            return isConfirmed;
          }),
          switchMap(() => this.subscriptionService.change(changeOptions)),
          tap(() => this.alertsService.addSuccess(`subscription updated successfully.`, 'Success: ')),
          finalize(() => this.unblockActions()),
        ).subscribe();

      return;
    }

    this.confirmationModalService.confirm(options)
      .pipe(
        filter(({ isConfirmed }) => isConfirmed),
        switchMap(() => this.subscriptionService.change(changeOptions)),
        tap(() => this.alertsService.addSuccess('trial period started successfully.')),
        finalize(() => this.unblockActions()),
      ).subscribe();
  }

  private switchCurrentPaymentPlan(plan: string): void {
    const pricePlan = this.pricePlansService.getByPlanCode(plan, this.isNotUsaOrCanadaRestaurant);
    const options = {
      title: 'Update your subscription',
      content: this.billingService.prepareChangeSubscriptionPlanConfirmationText(
        pricePlan,
        this.currentPricePlan,
        this.account.features,
        this.location.usage.total,
        this.account.recurly.trial === true,
      ),
      confirmBtnText: 'Confirm',
      closeBtnText: 'Cancel',
    }

    this.confirmationModalService.confirm(options)
      .pipe(
        filter(({ isConfirmed }) => {
          if (!isConfirmed) {
            this.unblockActions();
          }

          return isConfirmed;
        }),
        switchMap(() => this.subscriptionService.change({
          planCode: plan,
          uuid: this.currentSubscription.uuid,
        })),
        tap(() => {
          this.alertsService.addSuccess(`subscription updated successfully.`, 'Success: ');
          this.unblockActions();
        }),
        catchError((err) => {
          this.alertsService.addError(err.error.message);
          this.unblockActions();

          return of(err);
        }),
      ).subscribe();
  }
}
